import type { ReactElement } from "react";
import { glyphClass, type GlyphName } from "./Glyph.tsx";

/**
 * The kit's pending spinner (P2.8-T41, ADR-061's note of that row): a ring 8 logical pixels square, original art
 * under `src/ui/art/marks/glyph/` drawn in the current text colour, with a gap of four pixels that steps two pixels
 * round it every `--ot-dur-spinner-step` (two UI frames), a turn in ten frames. The frames are separate glyphs
 * stacked in place, each shown for its step by a `steps(1, end)` keyframe, so every frame stays on whole pixels and
 * nothing is rotated or resampled. Under reduced motion it holds still as the whole ring. It sits in a slot's 16
 * pixel glyph box or on a line of caption text.
 *
 * A surface mounts it only once a request has waited `PENDING_SPINNER_DELAY_MS` (`pending.ts`) and unmounts it the
 * moment the answer arrives, so its animation runs only while it is shown.
 */

/**
 * The turning frames, in the order they are shown.
 */
const FRAMES = ["spinner-1", "spinner-2", "spinner-3", "spinner-4", "spinner-5", "spinner-6", "spinner-7", "spinner-8", "spinner-9", "spinner-10"] as const satisfies readonly GlyphName[];

/**
 * The spinner. With a label it is an image of that name, which a screen reader reads where a sentence would have
 * stood; without one it is decoration, hidden from assistive technology.
 *
 * @param props The `label` naming what waits, when the spinner alone says so.
 * @returns The spinner.
 * @example
 * <PendingSpinner label="Moving the Minor Healing Potion: waiting for the server." />
 */
export function PendingSpinner({ label }: { readonly label?: string }): ReactElement {
  const named = label === undefined ? { "aria-hidden": true } : { role: "img", "aria-label": label };
  return (
    <span className="ot-spinner" data-kit="spinner" {...named}>
      <span className={`ot-spinner__still ot-icon ${glyphClass("spinner")}`} />
      {FRAMES.map((frame) => (
        <span key={frame} className={`ot-spinner__frame ot-icon ${glyphClass(frame)}`} />
      ))}
    </span>
  );
}
